import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  ListTodo, CheckCircle2, CalendarClock, AlertTriangle, Ban, Flame, ShieldCheck, TrendingUp,
} from "lucide-react";

const CARDS = [
  { key: "total_open", label: "Total Open", icon: ListTodo, color: "#2563EB", nav: "/tasks" },
  { key: "completed", label: "Completed", icon: CheckCircle2, color: "#16A34A", nav: "/tasks?execution_status=Completed" },
  { key: "due_today", label: "Due Today", icon: CalendarClock, color: "#CA8A04", nav: "/tasks?due=today" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, color: "#C2410C", nav: "/tasks?overdue=1" },
  { key: "blocked", label: "Blocked", icon: Ban, color: "#334155", nav: "/tasks?blocked=1" },
  { key: "critical", label: "Critical", icon: Flame, color: "#DC2626", nav: "/tasks?priority=Critical" },
  { key: "pending_approval", label: "Pending Approval", icon: ShieldCheck, color: "#1E40AF", nav: "/approvals" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-6 text-slate-400">Loading…</div>;
  const statusData = Object.entries(data.by_status).map(([name, value]) => ({ name, value }));
  const prioColors = { Critical: "#DC2626", High: "#EA580C", Medium: "#2563EB", Low: "#64748B" };
  const prioData = Object.entries(data.by_priority).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Dashboard" subtitle="Organization-wide execution overview" />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
        {CARDS.map((c) => (
          <button
            key={c.key}
            data-testid={`kpi-card-${c.key.replace(/_/g, "-")}`}
            onClick={() => nav(c.nav)}
            className="kpi-hover bg-white border rounded-xl p-4 text-left"
          >
            <div className="flex items-center justify-between">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.color + "15" }}>
                <c.icon size={16} style={{ color: c.color }} />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-2 font-mono">{data.kpis[c.key]}</div>
            <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-700 mb-1">On-Time Completion</div>
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-600" />
            <span className="text-3xl font-bold font-mono">{data.kpis.on_time_pct}%</span>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-700 mb-1">Due This Week</div>
          <span className="text-3xl font-bold font-mono">{data.kpis.due_this_week}</span>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-700 mb-1">Tasks Without Update</div>
          <span className="text-3xl font-bold font-mono">{data.kpis.no_update}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">Tasks by Status</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">Tasks by Priority</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={prioData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {prioData.map((e) => <Cell key={e.name} fill={prioColors[e.name] || "#94A3B8"} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
